import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHero, StatusPill } from "@/components/shared/Visual";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, MapPin, CalendarDays, Users2, FileText, MessageSquare, Send,
  AlertTriangle, ShieldAlert, Download, Lock, ChevronRight, Phone,
} from "lucide-react";

const LABEL = {
  awaiting_ops_approval: "Awaiting Approval", accepted: "Accepted", team_being_built: "Team Being Built",
  staff_awaiting_confirmation: "Staff Awaiting Confirmation", deployment_confirmed: "Deployment Confirmed",
  staff_en_route: "Staff En Route", live: "Live", completed: "Completed", closed: "Closed", declined: "Declined",
};
const KIND = {
  awaiting_ops_approval: "standby", accepted: "active", team_being_built: "tracked",
  staff_awaiting_confirmation: "tracked", deployment_confirmed: "tracked",
  staff_en_route: "live", live: "live", completed: "match", closed: "neutral", declined: "emergency",
};

export default function CompanyDeploymentDetail() {
  const { id } = useParams();
  const [dep, setDep] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [emOpen, setEmOpen] = useState(false);

  const load = () => api.get(`/managed-deployments/${id}`).then((r) => setDep(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, [id]);
  if (!dep) return <div className="p-8 txt-lo text-center">Loading…</div>;

  const active = ["accepted", "team_being_built", "staff_awaiting_confirmation", "deployment_confirmed", "staff_en_route", "live"].includes(dep.status);
  const readOnly = dep.status === "closed" || dep.status === "completed" || dep.status === "declined";

  const sendMsg = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { await api.post(`/managed-deployments/${id}/message`, { body: msg.trim() }); toast.success("Sent"); setMsg(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="company-dc-detail-page">
      <PageHero
        eyebrow="5% Managed Deployment"
        title={dep.job_title}
        sub={<span>{dep.location} · {dep.date}</span>}
        right={<StatusPill kind={KIND[dep.status]}>{LABEL[dep.status]}</StatusPill>}
      >
        <Link to="/company/deployment-centre" className="text-xs text-brand font-outfit font-bold uppercase tracking-widest flex items-center gap-1 hover:underline">
          <ArrowLeft className="w-3.5 h-3.5" /> Deployment Centre
        </Link>
      </PageHero>

      {/* Emergency ribbon */}
      {active && (
        <div className="emer-ribbon" data-testid="dc-emergency-ribbon">
          <AlertTriangle className="w-5 h-5" />
          <div className="flex-1">
            <div className="font-outfit font-bold">Emergency Contact</div>
            <div className="text-xs text-rose-200/80">Broadcast to the assigned deployment team.</div>
          </div>
          <Button onClick={() => setEmOpen(true)} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="dc-emergency-open">
            <ShieldAlert className="w-4 h-4 mr-1" /> Raise emergency
          </Button>
        </div>
      )}

      {readOnly && (
        <div className="alert-card info" data-testid="dc-readonly-banner">
          <Lock className="w-4 h-4 text-brand mt-0.5" />
          <div className="flex-1">
            <div className="font-outfit font-semibold txt-hi text-sm">Closed — read-only</div>
            <div className="eyebrow mt-1">All records preserved. Live operational controls unavailable.</div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Deployment" testId="dc-info-panel">
          <Row k="Job" v={dep.job_title} />
          <Row k="Location" v={dep.location} />
          <Row k="Date" v={`${dep.date} · ${dep.start_time || "—"}–${dep.end_time || "—"}`} />
          <Row k="Staff" v={`${dep.workers_needed}× ${dep.staff_type || "guard"}`} />
          <Row k="Admin" v={dep.assigned_admin_name || "—"} />
          <Row k="Status" v={LABEL[dep.status]} />
        </Panel>

        <Panel title={`Assigned Team (${(dep.team_members || []).length})`} testId="dc-team-panel">
          {(dep.team_members || []).length === 0 ? (
            <div className="txt-lo text-sm font-manrope">Team is being built.</div>
          ) : dep.team_members.map((m) => (
            <div key={m.user_id} className="card-inner p-2.5 flex items-center gap-2" data-testid={`dc-team-${m.user_id}`}>
              <div className="w-7 h-7 rounded-full bg-brand/15 text-brand flex items-center justify-center flex-shrink-0">
                <Users2 className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-outfit font-semibold txt-hi text-sm truncate">{m.name} {m.is_team_lead && <span className="spill spill-active ml-1"><span className="dot" /> Lead</span>}</div>
                <div className="eyebrow mt-0.5">{m.status}</div>
              </div>
            </div>
          ))}
        </Panel>

        <Panel title="SOPs & Documents" testId="dc-sops-panel">
          {(dep.sops || []).length === 0 ? <div className="txt-lo text-sm font-manrope">No documents yet.</div> : dep.sops.map((s) => (
            <div key={s.id} className="card-inner p-2.5">
              <div className="font-outfit font-semibold txt-hi text-sm">{s.title}</div>
              {s.notes && <div className="text-xs txt-lo font-manrope mt-0.5">{s.notes}</div>}
              {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-brand text-xs font-outfit font-semibold hover:underline mt-1 inline-block">Open →</a>}
            </div>
          ))}
        </Panel>
      </div>

      {/* Messages */}
      <div className="card-dark p-5" data-testid="dc-messages-panel">
        <div className="eyebrow eyebrow-brand flex items-center gap-2 mb-3"><MessageSquare className="w-3.5 h-3.5" /> Operations Messages</div>
        <div className="max-h-60 overflow-y-auto space-y-2 mb-3">
          {(dep.messages || []).length === 0 ? <div className="txt-lo text-sm font-manrope">No messages yet.</div> : dep.messages.map((m) => (
            <div key={m.id} className={`p-2 rounded ${m.author_role === "super_admin" ? "bg-brand/15 border border-brand/30" : "bg-white/5 border border-white/10"}`}>
              <div className="eyebrow mb-1">{m.author_name} · {new Date(m.created_at).toLocaleString()}</div>
              <div className="text-sm txt-hi font-manrope">{m.body}</div>
            </div>
          ))}
        </div>
        {!readOnly && dep.access_enabled && (
          <div className="flex gap-2">
            <Input placeholder="Reply to Operations…" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} data-testid="dc-msg-input" />
            <Button onClick={sendMsg} disabled={busy || !msg.trim()} className="btn-brand" data-testid="dc-msg-send"><Send className="w-4 h-4 mr-1" /> Send</Button>
          </div>
        )}
      </div>

      {/* Incidents */}
      {(dep.incidents || []).length > 0 && (
        <div className="card-dark p-5" data-testid="dc-incidents-panel">
          <div className="eyebrow eyebrow-brand mb-3">Incidents ({dep.incidents.length})</div>
          {dep.incidents.map((i) => (
            <div key={i.id} className="alert-card mb-2">
              <AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5" />
              <div className="flex-1">
                <div className="font-outfit font-semibold txt-hi text-sm">{i.report?.what_happened}</div>
                <div className="eyebrow mt-1">
                  {i.raised_by_name} · {new Date(i.created_at).toLocaleString()}
                  {i.escalated && <span className="ml-2 text-rose-300 font-outfit font-bold">· ESCALATED</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Final record download after closed/completed */}
      {(dep.status === "completed" || dep.status === "closed") && (
        <div className="card-dark p-5">
          <div className="eyebrow eyebrow-brand mb-3">Final Deployment Record</div>
          <Button variant="outline" className="btn-ghost-dark" data-testid="dc-final-download">
            <Download className="w-4 h-4 mr-1" /> Download final record (PDF)
          </Button>
        </div>
      )}

      {emOpen && <EmergencyDialog dep={dep} onClose={() => { setEmOpen(false); load(); }} />}
    </div>
  );
}

function Panel({ title, testId, children }) {
  return (
    <div className="card-dark p-5" data-testid={testId}>
      <div className="eyebrow eyebrow-brand mb-3">{title}</div>
      <div className="space-y-2 text-sm font-manrope">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return <div className="flex items-center justify-between text-sm"><span className="txt-lo">{k}</span><span className="txt-hi font-outfit font-semibold text-right ml-2">{v}</span></div>;
}

function EmergencyDialog({ dep, onClose }) {
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState({});
  const [escalate, setEscalate] = useState(false);
  const [report, setReport] = useState({ what_happened: "", location: dep.location, when: "", people: "", action: "" });
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    api.get(`/managed-deployments/${dep.id}/emergency-recipients`).then((r) => {
      const rec = r.data?.recipients || [];
      setRecipients(rec);
      const initial = {};
      rec.forEach((x) => { if (x.user_id) initial[x.user_id] = true; });
      setSelected(initial);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [dep.id]);

  const toggle = (uid) => setSelected((s) => ({ ...s, [uid]: !s[uid] }));

  const send = async (esc) => {
    if (!report.what_happened.trim()) { toast.error("Describe what happened"); return; }
    setBusy(true);
    try {
      const selIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
      const omitIds = Object.entries(selected).filter(([, v]) => !v).map(([k]) => k);
      await api.post(`/managed-deployments/${dep.id}/emergency`, {
        recipient_user_ids: selIds,
        omitted_user_ids: omitIds,
        escalate_to_secusource: !!esc,
        report_what_happened: report.what_happened,
        report_location: report.location,
        report_when: report.when || new Date().toISOString(),
        report_people: report.people,
        report_action: report.action,
      });
      toast.success(esc ? "Emergency sent · SecuSource escalated" : "Emergency sent to deployment team");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const selCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-600">
            <ShieldAlert className="w-5 h-5" /> Emergency Alert
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3" data-testid="em-step-recipients">
            <div className="text-sm text-white/70 font-manrope">Select recipients. All deployment team members are selected by default — untick anyone you want to omit.</div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto border rounded-md p-2">
              {recipients.length === 0 && <div className="text-white/60 text-sm">No team recipients yet.</div>}
              {recipients.map((r) => (
                <label key={r.user_id} className="flex items-center gap-3 p-2 rounded hover:bg-[#0E0E0E] cursor-pointer" data-testid={`em-recipient-${r.user_id}`}>
                  <Checkbox checked={!!selected[r.user_id]} onCheckedChange={() => toggle(r.user_id)} />
                  <div className="min-w-0 flex-1">
                    <div className="font-outfit font-semibold text-white text-sm truncate">{r.name}</div>
                    <div className="text-xs text-white/60">{r.role}{r.phone ? ` · ${r.phone}` : ""}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-outfit font-bold ${r.kind === "team_lead" ? "text-gold-500" : r.kind === "ops_admin" ? "text-brand" : "text-white/60"}`}>{r.kind.replace("_", " ")}</span>
                </label>
              ))}
            </div>
            <div className="text-xs text-white/60 font-manrope">{selCount} of {recipients.length} selected</div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={() => setStep(2)} disabled={selCount === 0} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="em-next">Next: Report</Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="em-step-report">
            <div>
              <Label>What happened <span className="text-rose-500">*</span></Label>
              <Textarea rows={3} value={report.what_happened} onChange={(e) => setReport({ ...report, what_happened: e.target.value })} data-testid="em-report-what" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Location</Label><Input value={report.location} onChange={(e) => setReport({ ...report, location: e.target.value })} data-testid="em-report-location" /></div>
              <div><Label>When (leave blank for now)</Label><Input type="datetime-local" value={report.when} onChange={(e) => setReport({ ...report, when: e.target.value })} /></div>
            </div>
            <div><Label>People involved</Label><Input value={report.people} onChange={(e) => setReport({ ...report, people: e.target.value })} data-testid="em-report-people" /></div>
            <div><Label>Action required</Label><Textarea rows={2} value={report.action} onChange={(e) => setReport({ ...report, action: e.target.value })} data-testid="em-report-action" /></div>

            <DialogFooter className="gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={() => send(false)} disabled={busy} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="em-send-team">
                Send to Selected Team
              </Button>
              <Button onClick={() => send(true)} disabled={busy} className="bg-navy-900 hover:bg-black text-white" data-testid="em-send-escalate">
                Send &amp; Escalate to SecuSource
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
